// src/pages/admin/StudentList.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { useNavigate } from 'react-router-dom';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import { fetchAllPages } from '../../api/fetchAllPages';
import { exportToExcel } from '../../utils/excelExport';
import { getUserListExportColumns } from '../../utils/userListExport';
import PaginationFooter from '../../components/common/PaginationFooter';
import UserLink from '../../components/common/UserLink';
import { formatFullName } from '../../utils/name';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import UserRowActions from '../../components/common/UserRowActions';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import ListRecordIdBadge from '../../components/common/ListRecordIdBadge';
import { getAdminLicense } from '../../api/license';
import { resolveRowId } from '../../utils/tableSort';

function useDebounced(value, delay = 400) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const StudentList = () => {
    const { t } = useTranslation();
    const [pageRows, setPageRows] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [tabCounts, setTabCounts] = useState({ all: 0, active: 0, archived: 0 });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 400);
    const [filter, setFilter] = useState('active');
    const navigate = useNavigate();

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [sortKey, setSortKey] = useState('id');
    const [sortDir, setSortDir] = useState('asc');
    const [studentLicenseAtLimit, setStudentLicenseAtLimit] = useState(false);
    const [exporting, setExporting] = useState(false);

    const handleExportStudents = useCallback(async () => {
        setExporting(true);
        try {
            const rows = await fetchAllPages((params) =>
                API.get('/admin/users/students/search', {
                    params: { q: dq, status: filter, sortBy: sortKey, sortDir, ...params },
                })
            );
            exportToExcel(getUserListExportColumns(t), rows, 'students');
        } catch (err) {
            console.error('Student Excel export failed:', err);
            alert(t('pages.students.exportFailed'));
        } finally {
            setExporting(false);
        }
    }, [dq, filter, sortKey, sortDir, t]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    // Fetch one page from server
    const fetchPage = useCallback((pg, sz, query, status, sortBy, sortDirection) => {
        setLoading(true);
        API.get('/admin/users/students/search', {
            params: {
                q: query,
                status,
                page: pg,
                size: sz,
                sortBy,
                sortDir: sortDirection,
            },
        }).then(res => {
            setPageRows(res.data.content || []);
            setTotalElements(res.data.totalElements || 0);
            setLoadError(false);
            setLoading(false);
        }).catch(err => {
            console.error('Error fetching students:', err);
            setPageRows([]);
            setTotalElements(0);
            setLoadError(true);
            setLoading(false);
        });
    }, []);

    // Fetch tab counts separately
    const fetchCounts = useCallback((query) => {
        Promise.all([
            API.get('/admin/users/students/search', { params: { q: query, status: 'all',      page: 0, size: 1 } }),
            API.get('/admin/users/students/search', { params: { q: query, status: 'active',   page: 0, size: 1 } }),
            API.get('/admin/users/students/search', { params: { q: query, status: 'archived', page: 0, size: 1 } }),
        ]).then(([all, active, archived]) => {
            setTabCounts({
                all:      all.data.totalElements || 0,
                active:   active.data.totalElements || 0,
                archived: archived.data.totalElements || 0,
            });
        }).catch(() => {});
    }, []);

    useEffect(() => {
        getAdminLicense()
            .then((data) => {
                const max = data?.limits?.maxStudents;
                const used = data?.usage?.students ?? 0;
                setStudentLicenseAtLimit(max != null && max >= 0 && used >= max);
            })
            .catch(() => setStudentLicenseAtLimit(false));
    }, []);

    useEffect(() => {
        fetchPage(page, size, dq, filter, sortKey, sortDir);
    }, [page, size, dq, filter, sortKey, sortDir, fetchPage]);

    useEffect(() => {
        fetchCounts(dq);
    }, [dq, fetchCounts]);

    // Reset to page 0 when search/filter/size/sort changes
    useEffect(() => { setPage(0); }, [dq, size, filter, sortKey, sortDir]);

    const refreshStudents = useCallback(() => {
        fetchPage(page, size, dq, filter, sortKey, sortDir);
        fetchCounts(dq);
        getAdminLicense()
            .then((data) => {
                const max = data?.limits?.maxStudents;
                const used = data?.usage?.students ?? 0;
                setStudentLicenseAtLimit(max != null && max >= 0 && used >= max);
            })
            .catch(() => {});
    }, [page, size, dq, filter, sortKey, sortDir, fetchPage, fetchCounts]);

    const studentFilterTabs = useMemo(() => [
        { key: "all", label: t('common.all'), count: tabCounts.all },
        { key: "active", label: t('common.active'), count: tabCounts.active },
        { key: "archived", label: t('common.archived'), count: tabCounts.archived },
    ], [tabCounts, t]);

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.students.title')}
                subtitle={t('pages.students.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            onExport={handleExportStudents}
                            filename="students"
                            disabled={exporting || totalElements === 0}
                            label={exporting ? t('common.exporting') : t('common.downloadExcel')}
                        />
                        <button
                            type="button"
                            onClick={() => navigate('/admin/students/bulk-import')}
                            className="btn-page-action-accent"
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
                            </svg>
                            {t('pages.students.bulkImport')}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/students/new')}
                            className="btn-page-action-primary"
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('pages.students.add')}
                        </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={studentFilterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.students.searchPlaceholder')}
                shown={pageRows.length}
                total={totalElements}
                entityLabel={t('pages.students.entityLabel')}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : pageRows.length === 0 ? (
                <ListEmptyState
                    message={loadError
                        ? t('common.listLoadFailed')
                        : (q ? t('common.noMatchSearch', { entity: t('pages.students.entityLabel') }) : t('common.noFound', { entity: t('pages.students.entityLabel') }))}
                    hint={!loadError && q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <>
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortableTableHeader label={t('pages.students.colStudentName')} columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                                    <th className="table-header-cell">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pageRows.map(student => (
                                    <tr key={String(student.id ?? student.user_id)} className="table-row">
                                        <td className="table-cell whitespace-nowrap">
                                            <ListRecordIdBadge id={resolveRowId(student)} />
                                        </td>
                                        <td className="table-cell">
                                            {formatFullName(student.firstName, student.middleName, student.lastName) ? (
                                                <UserLink
                                                    id={student.id}
                                                    name={formatFullName(student.firstName, student.middleName, student.lastName)}
                                                />
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="table-cell">{student.username || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{student.email || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{student.phoneNumber || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={student.active} />
                                        </td>
                                        <td className="table-cell whitespace-nowrap">
                                            <UserRowActions
                                                user={student}
                                                entityLabel="student"
                                                editPath={`/admin/students/${student.id}`}
                                                onStatusChange={refreshStudents}
                                                licenseAtLimit={studentLicenseAtLimit}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </ListTableCard>

                <PaginationFooter
                    page={page}
                    size={size}
                    total={totalElements}
                    onPageChange={setPage}
                    onSizeChange={(v) => { setSize(v); setPage(0); }}
                />
                </>
            )}
        </ListPageLayout>
    );
};

export default StudentList;
